import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { WidgetProps } from './registry';
import { decodeWidgetData } from './decode-widget-data';

type Item = { prospectId: string; handle?: string };
type Phases = Record<string, Item[]>;

// Stable column order following the v7 outreach lifecycle phases.
// Phases not in this list are appended at the end.
const PHASE_ORDER = ['discovered', 'scored', 'qualified', 'drafted', 'contacted', 'engaged', 'interested', 'converted', 'cold', 'dismissed'];

export function PipelineView(props: WidgetProps) {
  const phases = (decodeWidgetData(props.payload).phases ?? {}) as Phases;
  const [acted, setActed] = useState<Record<string, 'pending' | 'qualified' | 'dismissed' | 'error'>>({});
  const navigate = useNavigate();

  // H.2.4: /outreach/prospects/:id retired — prospect links now scope the chat.
  const scopeToProspect = (prospectId: string) => {
    sessionStorage.setItem('outreach-scope', JSON.stringify({ prospectId }));
    navigate('/outreach');
  };

  const act = async (prospectId: string, kind: 'qualify' | 'dismiss') => {
    setActed((s) => ({ ...s, [prospectId]: 'pending' }));
    let res: Response | null = null;
    try {
      res = await fetch(`/admin/prospects/${encodeURIComponent(prospectId)}/${kind}`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({}),
      });
    } catch {
      // Network failure — leave retry-able
    }
    setActed((s) => ({
      ...s,
      [prospectId]: res && res.ok
        ? (kind === 'qualify' ? 'qualified' : 'dismissed')
        : 'error',
    }));
  };

  const phaseNames = Object.keys(phases).sort((a, b) => {
    const ai = PHASE_ORDER.indexOf(a);
    const bi = PHASE_ORDER.indexOf(b);
    if (ai === -1 && bi === -1) return a.localeCompare(b);
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });

  if (phaseNames.length === 0) {
    return <p className="text-sm text-foreground-mute">No prospects yet.</p>;
  }

  return (
    <div className="grid grid-flow-col auto-cols-[12rem] gap-2 overflow-x-auto py-1">
      {phaseNames.map((phase) => (
        <div key={phase} className="rounded border border-border p-2 bg-card/30">
          <div className="text-[10px] uppercase tracking-wider text-foreground-mute mb-2 flex justify-between">
            <span>{phase}</span>
            <span>{phases[phase]!.length}</span>
          </div>
          <ul className="space-y-1">
            {phases[phase]!.map((it) => {
              const decision = acted[it.prospectId] as
                | 'pending'
                | 'qualified'
                | 'dismissed'
                | 'error'
                | undefined;
              const isScored = phase === 'scored';

              return (
                <li key={it.prospectId} className="space-y-1">
                  <button
                    type="button"
                    onClick={() => scopeToProspect(it.prospectId)}
                    className="block w-full text-left text-xs px-2 py-1 rounded bg-background hover:bg-muted truncate"
                  >
                    {it.handle ? `@${it.handle}` : it.prospectId}
                  </button>
                  {isScored && decision !== 'qualified' && decision !== 'dismissed' && (
                    <div className="flex gap-1 px-1">
                      <button
                        type="button"
                        onClick={() => act(it.prospectId, 'qualify')}
                        disabled={decision === 'pending'}
                        className="flex-1 rounded bg-primary px-1 py-0.5 text-[10px] text-primary-foreground disabled:opacity-40"
                        data-testid={`pipeline-qualify-${it.prospectId}`}
                      >
                        {decision === 'pending' ? '…' : 'Qualify'}
                      </button>
                      <button
                        type="button"
                        onClick={() => act(it.prospectId, 'dismiss')}
                        disabled={decision === 'pending'}
                        className="flex-1 rounded border border-border bg-background px-1 py-0.5 text-[10px] text-foreground disabled:opacity-40"
                        data-testid={`pipeline-dismiss-${it.prospectId}`}
                      >
                        {decision === 'pending' ? '…' : 'Dismiss'}
                      </button>
                    </div>
                  )}
                  {isScored && (decision === 'qualified' || decision === 'dismissed') && (
                    <p className="px-1 text-[10px] text-foreground-mute">
                      {decision} ✓
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
}
